import { useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useErrorStore } from '@/store/errorStore';
import { AccessDenied } from '@/pages/ui-pages/AccessDenied';
import { ServerError } from '@/pages/ui-pages/ServerError';

interface ErrorHandlerProps {
  children: ReactNode;
}

/**
 * ErrorHandler component that wraps routes and displays appropriate error pages
 * based on the global error store. It clears errors on route change.
 */
export const ErrorHandler = ({ children }: ErrorHandlerProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { error, clearError } = useErrorStore();

  // Clear error when route changes
  useEffect(() => {
    if (error) {
      clearError();
    }
    // Only clear on location change, not on error change
  }, [location.pathname]);

  // Handle unauthorized - redirect to login
  useEffect(() => {
    if (error?.type === 'unauthorized') {
      const currentPath = location.pathname + location.search;
      const returnUrl = encodeURIComponent(currentPath);
      navigate(`/login?redirect=${returnUrl}`, { replace: true });
      clearError();
    }
  }, [error, location, navigate, clearError]);

  // Render error pages based on error type
  if (error) {
    switch (error.type) {
      case 'forbidden':
        return <AccessDenied />;

      case 'server_error':
        return <ServerError />;

      case 'not_found':
        // Let the router handle 404s naturally
        return <>{children}</>;

      case 'unauthorized':
        // Handled by useEffect above, but show nothing while redirecting
        return null;

      default:
        return <>{children}</>;
    }
  }

  return <>{children}</>;
};

export default ErrorHandler;
